import { Injectable, inject } from '@angular/core';
import {
  Storage,
  getDownloadURL,
  ref,
  uploadBytesResumable,
} from '@angular/fire/storage';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Media, Patient } from '@principle-theorem/principle-core';
import {
  IEmailAttachment,
  IMedia,
  IPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  INamedDocument,
  WithRef,
  isSameRef,
} from '@principle-theorem/shared';
import { type IResolvedAttachment } from './interaction-timeline-event.component';

@Injectable({
  providedIn: 'root',
})
export class EmailAttachmentMediaService {
  private _storage = inject(Storage);
  private _snackBar = inject(MatSnackBar);

  async downloadAttachment(
    media: WithRef<IMedia>,
    filename: string
  ): Promise<void> {
    try {
      await Media.downloadMediaFile(this._storage, media.path, filename);
    } catch {
      this._snackBar.open('Failed to download attachment');
    }
  }

  async copyToPatientFiles(
    patient: INamedDocument<IPatient>,
    resolved: IResolvedAttachment
  ): Promise<boolean> {
    try {
      const uploadPath = Patient.storagePath(patient);
      const newPath = this._generateMediaPath(uploadPath, resolved.media.name);

      await this._downloadAndUploadMedia(resolved.media.path, newPath);

      const patientMediaRef = await Firestore.addDoc(
        Patient.mediaCol(patient),
        Media.init({
          name: resolved.attachment.name,
          path: newPath,
          tags: [],
          metadata: {
            type: resolved.attachment.type || 'application/octet-stream',
            size: resolved.attachment.size ?? 0,
          },
        })
      );

      await this._updateEmailAttachmentRef(
        resolved.attachment,
        patientMediaRef
      );

      this._snackBar.open('Attachment copied to patient files');
      return true;
    } catch {
      this._snackBar.open('Failed to copy attachment to patient files');
      return false;
    }
  }

  isInPatientFiles(
    attachment: IEmailAttachment,
    patient?: INamedDocument<IPatient>
  ): boolean {
    if (attachment.patientMediaRef) {
      return true;
    }
    if (attachment.savedToPatientFiles) {
      return true;
    }
    if (patient && attachment.mediaRef) {
      const patientMediaColPath = Patient.mediaCol(patient).path;
      return attachment.mediaRef.parent.path === patientMediaColPath;
    }
    return false;
  }

  private async _updateEmailAttachmentRef(
    attachment: IEmailAttachment,
    patientMediaRef: DocumentReference<IMedia>
  ): Promise<void> {
    if (!attachment.emailRef || !attachment.mediaRef) {
      return;
    }

    const email = await Firestore.getDoc(attachment.emailRef);
    const emailAttachments: DocumentReference<IMedia>[] =
      (email as { attachments?: DocumentReference<IMedia>[] }).attachments ??
      [];

    const updatedAttachments = emailAttachments.map((existingRef) =>
      isSameRef(existingRef, attachment.mediaRef)
        ? patientMediaRef
        : existingRef
    );

    await Firestore.patchDoc(attachment.emailRef, {
      attachments: updatedAttachments,
    });
  }

  private async _downloadAndUploadMedia(
    sourcePath: string,
    destinationPath: string
  ): Promise<void> {
    const url = await getDownloadURL(ref(this._storage, sourcePath));
    const response = await fetch(url);
    const blob = await response.blob();

    const uploadTask = uploadBytesResumable(
      ref(this._storage, destinationPath),
      blob
    );

    return new Promise<void>((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        undefined,
        (error) => reject(error),
        () => resolve()
      );
    });
  }

  private _generateMediaPath(basePath: string, fileName: string): string {
    const timestamp = Date.now();
    return `${basePath}/${timestamp}_${fileName}`;
  }
}
